import { mergeCompanySettingsWithDefaults } from '../../../core/company/companyConfig';
import {
  getRouteStatus,
  normalizeLiquidationSettings
} from '../../logistics/components/LogisticsUtils';
import {
  getTransportLabel,
  getTransportVisualConfig,
  hasValidLatLng
} from '../../logistics/components/mapUtils';
import { resolveTransportIdentity } from '../../logistics/components/transportIdentity';

export const OFFICE_LOCATION = {
  lat: -34.724019,
  lng: -58.397501,
  name: 'Oficina Central',
  address: ''
};


export const formatCurrency = (value) => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return '0,00';
  }
  return value.toLocaleString('es-AR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

export const getLocalDateAsString = (date) => {
  if (!date) return '';
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const ACTIVE_ROUTE_STATUSES = new Set([
  'active',
  'activa',
  'activo',
  'in_progress',
  'in-progress',
  'en_curso',
  'en-curso',
  'started',
  'pending',
  'pendiente',
  'assigned',
  'asignada',
  'asignado'
]);

export const isRouteOperationallyActive = (route, today = getLocalDateAsString(new Date())) => {
  if (!route || typeof route !== 'object') return false;
  if (route.date !== today) return false;

  const normalizedStatus = getRouteStatus(route);

  if (normalizedStatus === 'completed' || normalizedStatus === 'cancelled') {
    return false;
  }

  if (route.active === true || route.isActive === true) return true;

  return ACTIVE_ROUTE_STATUSES.has(normalizedStatus);
};

export const normalizeNumber = (value, fallback = 0) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : fallback;
  }

  if (typeof value === 'string') {
    const normalizedText = value.trim();
    if (!normalizedText) return fallback;

    const sanitized = normalizedText
      .replace(/\s+/g, '')
      .replace(/\.(?=\d{3}(?:\D|$))/g, '')
      .replace(',', '.');

    const parsed = Number(sanitized);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const normalizeCostScales = (costScales = []) => {
  if (!Array.isArray(costScales)) return [];

  return costScales
    .map((scale) => ({
      from: normalizeNumber(scale?.from, 0),
      to: normalizeNumber(scale?.to, 0),
      price: normalizeNumber(scale?.price, 0)
    }))
    .filter((scale) => scale.to > scale.from);
};

export const getOfficeLocationFromSettings = (settings = {}) => {
  const office = settings?.officeLocation || settings?.office || {};
  const name = office.name || office.officeName || OFFICE_LOCATION.name;
  const address = office.address || office.fullAddress || OFFICE_LOCATION.address;
  const shortName = office.shortName || String(name || 'Base').trim().slice(0, 2).toUpperCase();

  return {
    name,
    shortName,
    address,
    lat: normalizeNumber(office.lat, OFFICE_LOCATION.lat),
    lng: normalizeNumber(office.lng, OFFICE_LOCATION.lng)
  };
};

export const getNormalizedLiquidationSettings = (settings = {}) => {
  const nested = settings?.liquidationSettings || {};
  const mergedSource = {
    ...settings,
    ...nested
  };

  const normalizedByType = normalizeLiquidationSettings(settings);

  const normalizedScales = normalizeCostScales(
    mergedSource.costScales || settings.costScales || []
  );

  return {
    ...normalizedByType,
    enabled: normalizedByType.enabled !== false,
    costModel: mergedSource.costModel === 'byScale' ? 'byScale' : 'perKm',
    pricePerKm: normalizeNumber(
      mergedSource.pricePerKm,
      normalizedByType.defaultPricePerKm || 0
    ),
    costScales: normalizedScales,
    baseRouteFee: normalizeNumber(mergedSource.baseRouteFee, 0),
    pricePerCompletedStop: normalizeNumber(mergedSource.pricePerCompletedStop, 0),
    pricePerPickup: normalizeNumber(mergedSource.pricePerPickup, 0),
    urgentSurcharge: normalizeNumber(mergedSource.urgentSurcharge, 0),
    incompleteMultiplier: normalizeNumber(mergedSource.incompleteMultiplier, 0.5),
    cancelledMultiplier: normalizeNumber(mergedSource.cancelledMultiplier, 0)
  };
};

export const buildMonitorSettings = ({
  companyId,
  companyName,
  mainSettings,
  liquidationSettings,
  officeSettings,
  modulesSettings,
  logisticsMonitorSettings,
  legacySettings
}) => {
  const mergedWithDefaults = mergeCompanySettingsWithDefaults(
    {
      companyId,
      companyName,
      ...(mainSettings || {}),
      modules: {
        ...(mainSettings?.modules || {}),
        ...(modulesSettings || {})
      },
      officeLocation: {
        ...(mainSettings?.officeLocation || {}),
        ...(officeSettings || {})
      },
      liquidationSettings: {
        ...(legacySettings || {}),
        ...(logisticsMonitorSettings || {}),
        ...(mainSettings?.liquidationSettings || {}),
        ...(liquidationSettings || {})
      }
    },
    companyId
  );

  const normalizedLiquidation = getNormalizedLiquidationSettings(mergedWithDefaults);
  const normalizedOffice = getOfficeLocationFromSettings(mergedWithDefaults);

  return {
    ...mergedWithDefaults,
    ...(legacySettings || {}),
    ...(logisticsMonitorSettings || {}),
    companyId,
    companyName: mergedWithDefaults.companyName || companyName,
    officeLocation: normalizedOffice,
    liquidationSettings: normalizedLiquidation,

    // Compatibilidad con el código actual
    costModel: normalizedLiquidation.costModel,
    pricePerKm: normalizedLiquidation.pricePerKm,
    costScales: normalizedLiquidation.costScales
  };
};

export const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

export const getTimestampMillis = (value) => {
  if (!value) return 0;
  if (typeof value?.toDate === 'function') return value.toDate().getTime();
  if (value instanceof Date) return value.getTime();
  if (typeof value?.seconds === 'number') return value.seconds * 1000;
  return 0;
};

 

export const getRouteTotalDistance = (route, settings) => {
  const officeLocation = getOfficeLocationFromSettings(settings);

  if (!route || !route.destinations) return '0.00';

  const completedDestinations = route.destinations
    .filter(
      (d) =>
        (d.status === 'completed' || d.status === 'incomplete') &&
        d.completedAt &&
        d.latlng &&
        typeof d.latlng.lat === 'number' &&
        typeof d.latlng.lng === 'number'
    )
    .sort((a, b) => getTimestampMillis(a.completedAt) - getTimestampMillis(b.completedAt));

  if (completedDestinations.length === 0) return '0.00';

  let totalDistance = 0;
  let lastPointCoords = { lat: officeLocation.lat, lng: officeLocation.lng };

  completedDestinations.forEach((dest) => {
    const currentDestCoords = dest.latlng;
    totalDistance += calculateDistance(
      lastPointCoords.lat,
      lastPointCoords.lng,
      currentDestCoords.lat,
      currentDestCoords.lng
    );
    lastPointCoords = currentDestCoords;
  });

  return totalDistance.toFixed(2);
};

export const getRouteSortTime = (route) => {
  if (route?.createdAt?.toDate) return route.createdAt.toDate().getTime();
  if (route?.updatedAt?.toDate) return route.updatedAt.toDate().getTime();
  if (route?.date) return new Date(`${route.date}T00:00:00`).getTime();
  return 0;
};

export const sortRoutesDesc = (routes = []) => {
  return [...routes].sort((a, b) => getRouteSortTime(b) - getRouteSortTime(a));
};

export const sortExpensesDesc = (expenses = []) => {
  return [...expenses].sort((a, b) => {
    const dateA = a?.date ? new Date(`${a.date}T00:00:00`).getTime() : 0;
    const dateB = b?.date ? new Date(`${b.date}T00:00:00`).getTime() : 0;
    return dateB - dateA;
  });
};

// --- Helpers monitor en vivo ---
export const getTransportSpeedKmh = (transportType = 'camioneta') => {
  const normalized = String(transportType || 'camioneta').toLowerCase();
  const speeds = {
    moto: 32,
    camioneta: 38,
    auto: 42,
    camion: 30
  };

  return speeds[normalized] || 35;
};

export const getRelativeTimeLabel = (timestampMillis) => {
  if (!timestampMillis) return 'Sin datos';

  const diffMinutes = Math.max(0, Math.round((Date.now() - timestampMillis) / 60000));

  if (diffMinutes <= 1) return 'Hace instantes';
  if (diffMinutes < 60) return `Hace ${diffMinutes} min`;

  const hours = Math.floor(diffMinutes / 60);
  const minutes = diffMinutes % 60;
  if (hours < 24) return `Hace ${hours}h ${minutes}m`;

  return new Date(timestampMillis).toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const normalizeLivePath = (path = []) =>
  (Array.isArray(path) ? path : []).filter((point) => hasValidLatLng(point));

export const getPendingDestinations = (route) =>
  [...(route?.destinations || [])]
    .filter((dest) => dest.status === 'pending')
    .sort((a, b) => (a.order || 0) - (b.order || 0));

export const getLastCompletedDestination = (route) =>
  [...(route?.destinations || [])]
    .filter((dest) => ['completed', 'incomplete'].includes(dest.status) && hasValidLatLng(dest.latlng))
    .sort((a, b) => getTimestampMillis(b.completedAt) - getTimestampMillis(a.completedAt))[0] || null;

export const getNextLiveDestination = (route) => {
  const activeDestination = (route?.destinations || []).find((dest) => dest.status === 'en_camino');
  if (activeDestination) return activeDestination;

  const atDestination = (route?.destinations || []).find((dest) => dest.status === 'en_destino');
  if (atDestination) return atDestination;

  return getPendingDestinations(route)[0] || null;
};

export const getObservedSpeedKmh = (transporterPath = []) => {
  const validPath = normalizeLivePath(transporterPath);
  if (validPath.length < 2) return 0;

  const lastPoint = validPath[validPath.length - 1];
  const previousPoint = validPath[validPath.length - 2];

  const lastTimestamp = getTimestampMillis(lastPoint?.timestamp);
  const previousTimestamp = getTimestampMillis(previousPoint?.timestamp);

  if (!lastTimestamp || !previousTimestamp || lastTimestamp <= previousTimestamp) return 0;

  const distanceKm = calculateDistance(
    Number(previousPoint.lat),
    Number(previousPoint.lng),
    Number(lastPoint.lat),
    Number(lastPoint.lng)
  );
  const hours = (lastTimestamp - previousTimestamp) / 3600000;

  if (!hours || hours <= 0) return 0;

  return distanceKm / hours;
};

export const formatEtaLabel = (etaMinutes) => {
  if (!Number.isFinite(etaMinutes) || etaMinutes < 0) return 'N/D';
  if (etaMinutes < 1) return '< 1 min';
  if (etaMinutes < 60) return `${Math.round(etaMinutes)} min`;

  const hours = Math.floor(etaMinutes / 60);
  const minutes = Math.round(etaMinutes % 60);
  return `${hours}h ${minutes}m`;
};

export const buildLiveStatus = ({ currentLocation, nextDestination, observedSpeedKmh, lastUpdateMs, etaMinutes }) => {
  if (!currentLocation) {
    return {
      key: 'no_position',
      label: 'Sin posición',
      color: '#64748b',
      softColor: '#f1f5f9'
    };
  }

  const minutesSinceUpdate = lastUpdateMs
    ? (Date.now() - lastUpdateMs) / 60000
    : Number.POSITIVE_INFINITY;

  if (minutesSinceUpdate > 20) {
    return {
      key: 'stale',
      label: 'Sin señal reciente',
      color: '#ef4444',
      softColor: '#fef2f2'
    };
  }

  if (nextDestination?.status === 'en_destino') {
    return {
      key: 'at_destination',
      label: 'En destino',
      color: '#0ea5e9',
      softColor: '#ecfeff'
    };
  }

  if (Number.isFinite(etaMinutes) && etaMinutes <= 10) {
    return {
      key: 'arriving',
      label: 'Próximo arribo',
      color: '#f59e0b',
      softColor: '#fffbeb'
    };
  }

  if (observedSpeedKmh >= 8) {
    return {
      key: 'moving',
      label: 'En movimiento',
      color: '#16a34a',
      softColor: '#f0fdf4'
    };
  }

  return {
    key: 'stopped',
    label: 'Detenido',
    color: '#8b5cf6',
    softColor: '#f5f3ff'
  };
};

export const buildLiveRouteSnapshot = (route, officeLocation = null) => {
  const transportKey = route?.transport || route?.transportType || 'camioneta';
  const transporterPath = normalizeLivePath(route?.transporterLivePath || []);
  const persistedCurrentLocation = hasValidLatLng(route?.transporterLastLocation)
    ? {
        lat: Number(route.transporterLastLocation.lat),
        lng: Number(route.transporterLastLocation.lng),
        timestamp: route.transporterLastLocation.timestamp || null
      }
    : transporterPath[transporterPath.length - 1] || null;

  const shouldUseOfficeAsProvisionalLocation =
    !persistedCurrentLocation &&
    hasValidLatLng(officeLocation) &&
    ['in_progress', 'pending', 'programmed'].includes(route?.status || 'pending');

  const currentLocation = persistedCurrentLocation ||
    (shouldUseOfficeAsProvisionalLocation
      ? {
          lat: Number(officeLocation.lat),
          lng: Number(officeLocation.lng),
          timestamp: null,
          isProvisional: true
        }
      : null);

  const nextDestination = getNextLiveDestination(route);
  const lastCompletedDestination = getLastCompletedDestination(route);
  const totalDestinations = Array.isArray(route?.destinations) ? route.destinations.length : 0;
  const completedDestinations = (route?.destinations || []).filter((dest) =>
    ['completed', 'incomplete', 'cancelled'].includes(dest.status)
  ).length;
  const progress = totalDestinations > 0 ? (completedDestinations / totalDestinations) * 100 : 0;

  const observedSpeedKmh = getObservedSpeedKmh(transporterPath);
  const fallbackSpeed = getTransportSpeedKmh(transportKey);
  const effectiveSpeed = observedSpeedKmh >= 8 && observedSpeedKmh <= 95 ? observedSpeedKmh : fallbackSpeed;

  let distanceToNextKm = null;
  if (currentLocation && hasValidLatLng(nextDestination?.latlng)) {
    distanceToNextKm = calculateDistance(
      Number(currentLocation.lat),
      Number(currentLocation.lng),
      Number(nextDestination.latlng.lat),
      Number(nextDestination.latlng.lng)
    );
  }

  const etaMinutes =
    Number.isFinite(distanceToNextKm) && effectiveSpeed > 0
      ? (distanceToNextKm / effectiveSpeed) * 60
      : null;

  const lastUpdateMs = getTimestampMillis(currentLocation?.timestamp) || getTimestampMillis(route?.updatedAt);
  const liveStatus = buildLiveStatus({
    currentLocation,
    nextDestination,
    observedSpeedKmh,
    lastUpdateMs,
    etaMinutes
  });

  const transportVisual = getTransportVisualConfig(transportKey);
  const accentStyle = resolveTransportIdentity(route);

  return {
    id: route.id,
    route,
    name: route.name || 'Ruta activa',
    transporterName: route.transporterName || route.driverName || route.choferName || 'Chofer sin asignar',
    driverPhotoURL:
      route.driverPhotoURL ||
      route.currentDriverPhotoURL ||
      route.photoURL ||
      '',
    transportKey,
    transportLabel: getTransportLabel(transportKey),
    transportVisual,
    monitorAccentColor: accentStyle.identityColor || transportVisual.color,
    monitorAccentSoftColor: accentStyle.identitySoftColor || transportVisual.softColor,
    totalDestinations,
    completedDestinations,
    progress,
    currentLocation,
    transporterPath,
    nextDestination,
    lastCompletedDestination,
    activeLeg: nextDestination
      ? {
          from: lastCompletedDestination
            ? { ...lastCompletedDestination, latlng: lastCompletedDestination.latlng }
            : { latlng: OFFICE_LOCATION, name: OFFICE_LOCATION.name },
          to: nextDestination
        }
      : null,
    distanceToNextKm,
    observedSpeedKmh,
    etaMinutes,
    etaLabel: formatEtaLabel(etaMinutes),
    lastUpdateMs,
    lastUpdateLabel: getRelativeTimeLabel(lastUpdateMs),
    liveStatusKey: liveStatus.key,
    liveStatusLabel: liveStatus.label,
    statusColor: liveStatus.color,
    statusSoftColor: liveStatus.softColor,
    destinations: route.destinations || []
  };
};

// --- Monitor en vivo ---
